import { nativeEvents } from '@rocketlane/rocket-llm';
import { AxiosHeaders } from 'axios';
import { API_NAMESPACE, API_VERSION, getCurrentApiHost } from 'config/environment';
import { ApiKey } from 'services';
export const convertPolicyResponseToSection = (policy, violationId)=>{
    const result = policy.reduce((acc, item)=>{
        var _ref;
        var _item_rules;
        if (!item.isViolated) {
            return acc;
        }
        const children = (_ref = (_item_rules = item.rules) === null || _item_rules === void 0 ? void 0 : _item_rules.filter((rule)=>rule.isViolated)) !== null && _ref !== void 0 ? _ref : [];
        if (children.length > 0) {
            acc.push({
                ...item,
                rules: children
            });
        }
        return acc;
    }, []);
    return {
        policy: result,
        violationId
    };
};
const parseProjectId = (rawProjectId)=>{
    if (rawProjectId == null) {
        return null;
    }
    const parsed = typeof rawProjectId === 'number' ? rawProjectId : Number.parseInt(String(rawProjectId), 10);
    return Number.isFinite(parsed) ? parsed : null;
};
/**
 * Axios config for replaying a bulk project mutation from the conflict modal.
 * Auth headers are attached so `retryOriginalRequest` (bare axios) succeeds.
 */ export const buildBulkProjectRequestConfig = (path, data)=>({
        method: 'put',
        url: path,
        baseURL: `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}`,
        data,
        headers: new AxiosHeaders(ApiKey.header())
    });
/**
 * Resolve which requested projects succeeded vs still need attention.
 *
 * Prefer `successfulProjectIds` when the backend sends them (including `[]`).
 * Otherwise treat anything not listed in `failedProjectIds` as succeeded.
 * Failed IDs are always the requested set minus successes — matching grid
 * patching, so projects omitted from both response lists stay conflicted.
 */ export const deriveBulkProjectOutcomeIds = (requestedProjectIds, response)=>{
    const successfulFromResponse = response === null || response === void 0 ? void 0 : response.successfulProjectIds;
    const successfulProjectIds = successfulFromResponse != null ? successfulFromResponse : requestedProjectIds.filter((id)=>{
        var _ref;
        return !((_ref = response === null || response === void 0 ? void 0 : response.failedProjectIds) !== null && _ref !== void 0 ? _ref : []).includes(id);
    });
    const succeededSet = new Set(successfulProjectIds);
    const failedProjectIds = requestedProjectIds.filter((id)=>!succeededSet.has(id));
    return {
        successfulProjectIds,
        failedProjectIds
    };
};
/**
 * Opens the bulk policy-conflict modal for project bulk update / archive.
 * Those endpoints return HTTP 200 with `policyEvaluationResponse` nested in
 * the body — they do not go through the 422 interceptor.
 *
 * Callers should pass IDs already normalized via
 * `deriveBulkProjectOutcomeIds` so the modal matches grid success/failure.
 */ export const handleBulkProjectPolicyConflict = (response, originalRequestConfig)=>{
    var _ref, _ref1;
    const evaluation = response === null || response === void 0 ? void 0 : response.policyEvaluationResponse;
    if (evaluation == null || evaluation.isViolated !== true) {
        return false;
    }
    const { ruleResults, executionId, projectId: rawProjectId } = evaluation;
    if (!Array.isArray(ruleResults) || ruleResults.length === 0) {
        return false;
    }
    const projectId = parseProjectId(rawProjectId);
    nativeEvents.publish('policyConflictModel', {
        show: true,
        policy: convertPolicyResponseToSection(ruleResults, executionId !== null && executionId !== void 0 ? executionId : 0),
        projectId,
        executionId,
        errorData: evaluation,
        originalRequestConfig,
        displayOnly: false,
        isBulk: true,
        failedProjectIds: (_ref = response === null || response === void 0 ? void 0 : response.failedProjectIds) !== null && _ref !== void 0 ? _ref : [],
        successfulProjectIds: (_ref1 = response === null || response === void 0 ? void 0 : response.successfulProjectIds) !== null && _ref1 !== void 0 ? _ref1 : []
    });
    return true;
};
/**
 * Handles 422 policy conflict errors by showing the policy conflict modal.
 * Extracts projectId, ruleResults, and violationId from the error response.
 * Also captures the original request config so the request can be retried
 * after the user resolves the conflicts.
 *
 * Used in the global axios interceptor for single-entity API calls. Bulk
 * project update/archive conflicts arrive as HTTP 200 — see
 * `handleBulkProjectPolicyConflict`.
 */ export const handlePolicyConflictError = (err)=>{
    var _ref;
    var _error_response;
    const error = err;
    const data = (_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.data;
    if (!data || typeof data !== 'object') {
        return;
    }
    const d = data;
    const { ruleResults, executionId, projectId: rawProjectId } = d;
    if (!Array.isArray(ruleResults) || ruleResults.length === 0) {
        return;
    }
    // projectId is null for operations where the entity doesn't exist yet (e.g. project creation).
    // In that case show the modal in display-only mode (no retry / resolve-with-AI).
    const displayOnly = rawProjectId == null;
    const projectId = parseProjectId(rawProjectId);
    if (!displayOnly && projectId == null) {
        return;
    }
    const originalRequestConfig = (_ref = err === null || err === void 0 ? void 0 : err.config) !== null && _ref !== void 0 ? _ref : null;
    nativeEvents.publish('policyConflictModel', {
        show: true,
        policy: convertPolicyResponseToSection(ruleResults, executionId !== null && executionId !== void 0 ? executionId : 0),
        projectId,
        executionId,
        errorData: d,
        originalRequestConfig,
        displayOnly,
        isBulk: false
    });
};
